import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdvertisements } from "../../store/actions/advertisementsActions";
import { setAdMedia } from "../../store/reducers/advertisementsReducers";
import {
    PLACEMENTS,
    cacheKey,
    normalizeSettings,
    playlistFor,
    remoteUrl,
} from "./adPlaylist";

// Как часто перечитываем рекламу без сигнала от сервера. Сигнал ads_updated
// приходит сразу после правки в панели, опрос — страховка для офлайна и
// старого сервера.
const REFRESH_MS = 10 * 60 * 1000;
const CACHE_KEY = "cachedAds";

const cacheApi = () => window.electronAPI?.ads;

function readCachedAds() {
    try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
        return cached && Array.isArray(cached.advertisements) ? cached : null;
    } catch {
        return null;
    }
}

// Держит рекламу свежей всё время работы киоска, а не только на главной:
// она нужна и заставке, и экрану печати. Ничего не рисует.
export default function AdsSync() {
    const dispatch = useDispatch();
    const advertisements = useSelector((s) => s.advertisements.advertisements);

    useEffect(() => {
        const cached = readCachedAds();
        if (cached) {
            dispatch({ type: "advertisements/fetch/fulfilled", payload: cached });
        }
        const refresh = () => {
            const deviceToken = localStorage.getItem("deviceToken");
            if (deviceToken) dispatch(fetchAdvertisements(deviceToken));
        };
        refresh();
        const timer = setInterval(refresh, REFRESH_MS);
        const unsubscribe = cacheApi()?.onAdsUpdated?.(refresh);
        return () => {
            clearInterval(timer);
            if (typeof unsubscribe === "function") unsubscribe();
        };
    }, [dispatch]);

    useEffect(() => {
        if (!advertisements) return;
        try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(advertisements));
        } catch {
            // переполненное хранилище — просто не кэшируем
        }
    }, [advertisements]);

    // Файлы — в локальный кэш Electron. Первый ответ приходит сразу (что уже
    // на диске), дальше Electron сообщает о каждом докачанном файле.
    useEffect(() => {
        const api = cacheApi();
        if (!api?.prepare) return undefined;
        const items = advertisements?.advertisements || [];
        let cancelled = false;
        const sync = async () => {
            try {
                const result = await api.prepare(
                    items.map(({ id, creative_id, ad_path, version }) => ({ id, creative_id, ad_path, version })),
                );
                if (!cancelled && result?.ok) dispatch(setAdMedia(result.files || {}));
            } catch {
                // Кэш недоступен — экран печати без рекламы, заставка с сети
            }
        };
        sync();
        const unsubscribe = api.onCacheUpdated?.(sync);
        return () => {
            cancelled = true;
            if (typeof unsubscribe === "function") unsubscribe();
        };
    }, [advertisements, dispatch]);

    return null;
}

// Плейлист места показа с готовыми адресами файлов.
//
// С кэшем Electron ролик показывается только скачанным: на экране печати
// недокачанное видео — это чёрный экран на все двадцать секунд. Заставке
// разрешено играть с сети, пока файл ещё качается: времени у неё достаточно.
// Без кэша (старая сборка Electron, браузер) — всегда с сети.
export function useAdPlaylist(placement) {
    const { advertisements, media } = useSelector((s) => s.advertisements);
    const hasCache = Boolean(cacheApi()?.supportsCache);

    return useMemo(() => {
        const settings = normalizeSettings(advertisements?.settings);
        const items = playlistFor(advertisements?.advertisements, placement)
            .map((ad) => {
                const local = media?.[cacheKey(ad)];
                if (local) return { ...ad, src: local };
                if (hasCache && placement === PLACEMENTS.print) return null;
                return { ...ad, src: remoteUrl(ad) };
            })
            .filter(Boolean);
        const enabled = placement === PLACEMENTS.print ? settings.print_enabled : true;
        return { items: enabled ? items : [], settings };
    }, [advertisements, media, placement, hasCache]);
}
